import { apiService } from '@/services/api'
import type {
  Candidate,
  CreateNeedData,
  InterviewData,
  Need,
  UpdateNeedData
} from '@/types/api'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface NeedsFilter {
  search: string
  status: string
  priority: string
  department: string
}

interface JobsFilter {
  search: string
  status: string
  type: string
  location: string
}

interface CandidatesFilter {
  search: string
  status: string
  experience: string
  skills: string[]
}

export interface JobLocal {
  id: string | number;
  jobId?: string;
  title: string;
  experienceLevel?: string;
  contractType?: string;
  location?: string;
  remotePolicy?: string;
  industry?: string;
  mainMissions?: string[];
  techStack?: string[];
  toolsAndMethods?: string[];
  candidateProfile?: any;
  benefits?: string[];
  applicationLink?: string;
  content?: string;
  besoinId?: number;
  department?: string;
  creationDate?: string;
  isValidate?: boolean;
  numberOfCandidates?: number;
  status?: string;
  description?: string;
  requirements?: string[];
  type?: string;
  salaryMin?: number;
  salaryMax?: number;
  applicationsCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export const useHRStore = defineStore('hr', () => {
  // State
  const needs = ref<Need[]>([])
  const jobs = ref<JobLocal[]>([])
  const candidates = ref<Candidate[]>([])
  const isLoading = ref<boolean>(false)
  const error = ref<string | null>(null)

  // Filters and pagination
  const needsFilter = ref<NeedsFilter>({
    search: '',
    status: '',
    priority: '',
    department: ''
  })

  const jobsFilter = ref<JobsFilter>({
    search: '',
    status: '',
    type: '',
    location: ''
  })

  const candidatesFilter = ref<CandidatesFilter>({
    search: '',
    status: '',
    experience: '',
    skills: []
  })

  // Computed
  const filteredNeeds = computed(() => {
    return needs.value.filter(need => {
      const matchesSearch = need.title.toLowerCase().includes(needsFilter.value.search.toLowerCase()) ||
        need.description.toLowerCase().includes(needsFilter.value.search.toLowerCase())
      const matchesStatus = !needsFilter.value.status || need.status === needsFilter.value.status
      const matchesPriority = !needsFilter.value.priority || need.priority === needsFilter.value.priority
      const matchesDepartment = !needsFilter.value.department || need.department === needsFilter.value.department

      return matchesSearch && matchesStatus && matchesPriority && matchesDepartment
    })
  })

  const filteredJobs = computed(() => {
    return jobs.value.filter(job => {
      const matchesSearch = job.title.toLowerCase().includes(jobsFilter.value.search.toLowerCase()) ||
        job.description.toLowerCase().includes(jobsFilter.value.search.toLowerCase())
      const matchesStatus = !jobsFilter.value.status || job.status === jobsFilter.value.status
      const matchesType = !jobsFilter.value.type || job.type === jobsFilter.value.type
      const matchesLocation = !jobsFilter.value.location || job.location === jobsFilter.value.location

      return matchesSearch && matchesStatus && matchesType && matchesLocation
    })
  })

  const filteredCandidates = computed(() => {
    return candidates.value.filter(candidate => {
      const matchesSearch = candidate.name.toLowerCase().includes(candidatesFilter.value.search.toLowerCase()) ||
        candidate.email.toLowerCase().includes(candidatesFilter.value.search.toLowerCase())
      const matchesStatus = !candidatesFilter.value.status || candidate.status === candidatesFilter.value.status
      const matchesExperience = !candidatesFilter.value.experience || candidate.experience === candidatesFilter.value.experience
      const matchesSkills = candidatesFilter.value.skills.length === 0 ||
        candidatesFilter.value.skills.some(skill => candidate.skills?.includes(skill))

      return matchesSearch && matchesStatus && matchesExperience && matchesSkills
    })
  })

  // Actions - Needs Management
  const fetchNeeds = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiService.hr.getNeeds()
      // Mapping pour compatibilité front
      needs.value = (Array.isArray(response.data) ? response.data : (response.data.needs ?? [])).map(n => ({
        ...n,
        department: n.department ?? '',
        title: n.title ?? '',
        description: n.description ?? '',
        positions: n.positions ?? 1, // valeur par défaut si absent
        manager: n.manager ?? '',
        status: n.status ?? '',
        createdAt: n.createdAt ? new Date(n.createdAt) : undefined
      }))
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la récupération des besoins:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const createNeed = async (needData: CreateNeedData) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.createNeed(needData)
      if (response.data.success) {
        needs.value.push(response.data.need)
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la création du besoin:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateNeed = async (id: string, needData: UpdateNeedData) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.updateNeed(id, needData)
      if (response.data.success) {
        const index = needs.value.findIndex(need => need.id === id)
        if (index !== -1) {
          needs.value[index] = response.data.need
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la mise à jour du besoin:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const deleteNeed = async (id: string) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.deleteNeed(id)
      if (response.data.success) {
        const index = needs.value.findIndex(need => need.id === id)
        if (index !== -1) {
          needs.value.splice(index, 1)
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la suppression du besoin:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const validateNeed = async (id: number) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.validateNeed(id)
      // Met à jour le need dans le tableau
      const index = needs.value.findIndex(n => n.id === id)
      if (index !== -1) {
        needs.value[index] = {
          ...needs.value[index],
          ...response.data
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const closeNeed = async (id: number) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.closeNeed(id)
      // Met à jour le need dans le tableau
      const index = needs.value.findIndex(n => n.id === id)
      if (index !== -1) {
        needs.value[index] = {
          ...needs.value[index],
          ...response.data
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Actions - Jobs Management
  const fetchJobs = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiService.hr.getJobs()
      // Mapping pour compatibilité front (conversion snake_case → camelCase)
      jobs.value = (Array.isArray(response.data) ? response.data : (response.data.jobs ?? [])).map((j: any) => ({
        id: j.id,
        jobId: j.job_id ?? j.jobId,
        title: j.title ?? '',
        experienceLevel: j.experience_level ?? j.experienceLevel ?? '',
        contractType: j.contract_type ?? j.contractType ?? '',
        location: j.location ?? '',
        remotePolicy: j.remote_policy ?? j.remotePolicy ?? '',
        industry: j.industry ?? '',
        mainMissions: j.main_missions ?? j.mainMissions ?? [],
        techStack: j.tech_stack ?? j.techStack ?? [],
        toolsAndMethods: j.tools_and_methods ?? j.toolsAndMethods ?? [],
        candidateProfile: j.candidate_profile ?? j.candidateProfile ?? {},
        benefits: j.benefits ?? [],
        applicationLink: j.application_link ?? j.applicationLink ?? '',
        content: j.content ?? '',
        besoinId: j.besoin_id ?? j.besoinId,
        department: j.department ?? '',
        creationDate: j.creation_date ?? j.creationDate ?? '',
        isValidate: j.validate ?? j.isValidate ?? false,
        numberOfCandidates: j.number_of_candidates ?? j.numberOfCandidates ?? 0,
        status: j.status ?? 'draft',
        description: j.description ?? '',
        requirements: j.requirements ?? [],
        type: j.type ?? j.contract_type ?? '',
        salaryMin: j.salary_min ?? j.salaryMin,
        salaryMax: j.salary_max ?? j.salaryMax,
        applicationsCount: j.applications_count ?? j.applicationsCount ?? 0,
        createdAt: j.created_at ? new Date(j.created_at) : (j.creation_date ? new Date(j.creation_date) : new Date()),
        updatedAt: j.updated_at ? new Date(j.updated_at) : undefined
      }))
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la récupération des postes:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Corrige les types et la gestion du CRUD jobs pour FichePosteDto
  const createJob = async (jobData: any) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.createJob(jobData)
      jobs.value.push(response.data)
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la création du poste:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateJob = async (id: string, jobData: any) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.updateJob(id, jobData)
      // id peut être string ou number côté backend, on convertit pour la comparaison
      const index = jobs.value.findIndex(job => String(job.id) === String(id))
      if (index !== -1) {
        // On remplace l'objet entier pour éviter les conflits de types
        jobs.value[index] = {
          ...jobs.value[index],
          ...response.data,
          id: response.data.id // conserve l'id du backend (string ou number)
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la mise à jour du poste:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const deleteJob = async (id: string) => {
    isLoading.value = true
    try {
      // On force l'id en string pour la compatibilité
      await apiService.hr.deleteJob(String(id))
      const index = jobs.value.findIndex(job => String(job.id) === String(id))
      if (index !== -1) {
        jobs.value.splice(index, 1)
      }
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la suppression du poste:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const publishJob = async (id: string, channels: string[]) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.publishJob(id, { channels })
      if (response.data.success) {
        const index = jobs.value.findIndex(job => job.id === id)
        if (index !== -1) {
          jobs.value[index] = { ...jobs.value[index], published: true, publishedChannels: channels }
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la publication du poste:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const validateJob = async (id: string | number) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.validateJob(id)
      // Mettre à jour le job local avec le statut validé
      const index = jobs.value.findIndex(job => String(job.id) === String(id))
      if (index !== -1) {
        jobs.value[index] = {
          ...jobs.value[index],
          ...response.data,
          isValidate: true,
          status: 'active' // Le job devient actif quand il est validé
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la validation du poste:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Actions - Candidates Management
  const fetchCandidates = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiService.hr.getCandidates()
      candidates.value = response.data.candidates || []
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la récupération des candidats:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateCandidateStatus = async (id: string, status: string, notes?: string) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.updateCandidateStatus(id, { status, notes })
      if (response.data.success) {
        const index = candidates.value.findIndex(candidate => candidate.id === id)
        if (index !== -1) {
          candidates.value[index] = { ...candidates.value[index], status, notes }
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la mise à jour du statut:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const scheduleInterview = async (candidateId: string, interviewData: InterviewData) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.scheduleInterview(candidateId, interviewData)
      if (response.data.success) {
        const index = candidates.value.findIndex(candidate => candidate.id === candidateId)
        if (index !== -1) {
          candidates.value[index] = {
            ...candidates.value[index],
            status: 'interview_scheduled',
            interview: response.data.interview
          }
        }
      }
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la planification de l\'entretien:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Analytics
  const getAnalytics = async () => {
    try {
      const response = await apiService.hr.getAnalytics()
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de la récupération des statistiques:', err)
      throw err
    }
  }

  // Reset filters
  const resetNeedsFilter = (): void => {
    needsFilter.value = {
      search: '',
      status: '',
      priority: '',
      department: ''
    }
  }

  const resetJobsFilter = (): void => {
    jobsFilter.value = {
      search: '',
      status: '',
      type: '',
      location: ''
    }
  }

  const resetCandidatesFilter = (): void => {
    candidatesFilter.value = {
      search: '',
      status: '',
      experience: '',
      skills: []
    }
  }

  const downloadNeedPdf = async (id: number) => {
    return await apiService.hr.downloadNeedPdf(id)
  }

  const uploadNeed = async (file: File, additionalData: { manager: string; department: string }) => {
    isLoading.value = true
    try {
      const response = await apiService.hr.uploadNeed(file, additionalData)
      needs.value.push(response.data)
      return response.data
    } catch (err: any) {
      error.value = err.message
      console.error('Erreur lors de l\'upload du besoin:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    needs,
    jobs,
    candidates,
    isLoading,
    error,

    // Filters
    needsFilter,
    jobsFilter,
    candidatesFilter,

    // Computed
    filteredNeeds,
    filteredJobs,
    filteredCandidates,

    // Actions - Needs
    fetchNeeds,
    createNeed,
    updateNeed,
    deleteNeed,
    validateNeed,
    closeNeed,
    uploadNeed,

    // Actions - Jobs
    fetchJobs,
    createJob,
    updateJob,
    deleteJob,
    publishJob,
    validateJob,

    // Actions - Candidates
    fetchCandidates,
    updateCandidateStatus,
    scheduleInterview,

    // Analytics
    getAnalytics,

    // Filters
    resetNeedsFilter,
    resetJobsFilter,
    resetCandidatesFilter,

    // Actions - Download
    downloadNeedPdf
  }
})
